// Shared design tokens for Budgit web and mobile apps
// This file provides a single source of truth for colors, spacing, typography, and design scales

// Brand colors that work in both light and dark modes
export const colors = {
  primary: '#FF5D82',      // Budgit pink - accent color
  onPrimary: '#FFFFFF',   // Text and glyphs on solid pink actions
  secondary: '#14143B',    // Dark navy blue - used for text in light mode, background in dark mode
  background: '#FFFFFF',   // White background (light mode default)
  white: '#FFFFFF',
  pinkWash: '#FFF1F5',   // Light pink chip / stat fill; always carries a navy label
  status: {
    ready: '#4CAF50',      // success green
    syncing: '#FF9800',    // warning orange
    error: '#FD5875',      // our pink
    unknown: '#9E9E9E',    // default gray
    // Severity aliases — same values. Callers that reached for the
    // comment-word (success / warning / neutral) used to get undefined.
    success: '#4CAF50',
    warning: '#FF9800',
    neutral: '#9E9E9E',
  },
  tint: {
    // Alert background tints derived from Figma (10% opacity semantic colors)
    ready: 'rgba(76, 175, 80, 0.1)',     // success
    syncing: 'rgba(255, 152, 0, 0.1)',   // warning
    error: 'rgba(253, 88, 117, 0.1)',    // error
    info: 'rgba(255, 93, 130, 0.1)',     // info
  },
  bitcoin: '#F7931A',      // Bitcoin orange
  danger: '#d9534f',       // Danger red for destructive actions (muted)
  reward: '#FFE14D',       // Lemon yellow (D234, Jared 2026-10-26): earned rewards (unit chest, badge medals) and
                           // the streak symbol once approved. Use with care (D136): never text, accents, sparkles,
                           // confetti or XP; try pink first. Navy glyphs only: 13.5:1. White is 1.3:1, never used.
  // Spending chart intensity uses the same pink alpha levels in both modes.
  spendingLow: 'rgba(255, 93, 130, 0.2)',
  spendingMedium: 'rgba(255, 93, 130, 0.45)',
  spendingHigh: 'rgba(255, 93, 130, 0.7)',
  
  // Light mode palette
  light: {
    background: '#FFFFFF',           // White
    surface: '#FFFFFF',              // White cards
    surfaceElevated: '#E1E8EE',      // Input fields, elevated elements (light gray)
    text: {
      primary: '#14143B',            // Dark navy
      secondary: '#666666',          // Medium grey
      tertiary: '#999999',           // Light grey
    },
    border: 'rgba(20, 20, 59, 0.1)', // Subtle dark borders
    navigationSelection: 'rgba(0, 0, 0, 0.1)', // Neutral native selection lens
    // DECISION: Figma has no 'grey5' token, but shipped code relies on it. Code wins.
    grey5: '#E1E8EE',                // Input backgrounds, dividers
    track: '#efefef',                // Progress ring tracks, empty spending graph cells
  },
  
  // Dark mode palette - navy-based, not black
  dark: {
    background: '#14143B',           // Dark navy (inverted from light text.primary)
    surface: '#1E1E4A',              // Slightly lighter navy for cards
    surfaceElevated: '#252560',      // Input fields, elevated elements
    text: {
      primary: '#FFFFFF',            // White
      secondary: '#B0B0C8',          // Soft lavender-grey
      tertiary: '#8080A0',           // Muted lavender
    },
    border: 'rgba(255, 255, 255, 0.1)', // Subtle light borders
    navigationSelection: 'rgba(255, 255, 255, 0.1)',
    // DECISION: Figma has no 'grey5' token, but shipped code relies on it. Code wins.
    grey5: '#2A2A5A',                // Input backgrounds, dividers
    track: '#2A2A5A',                // Progress ring tracks, empty spending graph cells
  },

  // Light-mode defaults at the top level so web (and anyone who reaches for
  // these names) reads the shared package instead of a fork or `undefined`.
  text: {
    primary: '#14143B',
    secondary: '#666666',
    tertiary: '#999999',
  },
  textPrimary: '#14143B',
  textSecondary: '#666666',
  error: '#FD5875',
  success: '#4CAF50',
  warning: '#FF9800',
  border: 'rgba(20, 20, 59, 0.1)',
  primaryDark: '#14143B',            // same as secondary — hover/contrast navy
  // DS-8 named category palette — B2 Saturated (docs/design/PLAN_OF_RECORD.md D57, Jared 2026-10-17).
  // Sixteen defaults behind the picker; free hex stays valid through normalizeCategoryColor.
  // Order matters: the first ten are CATEGORY_COLOR_OPTIONS in order, so existing picks stay
  // valid (the Figma generator asserts it). White glyphs on every chip.
  category: {
    pink: '#FF5D82',      // = primary
    navy: '#14143B',      // = secondary
    green: '#4CAF50',     // = status.ready
    amber: '#FF9800',     // = status.syncing
    bitcoin: '#F7931A',   // = bitcoin
    blue: '#4A90E2',
    teal: '#2BB3A3',
    violet: '#7A5CFF',
    rose: '#D93A5C',
    slate: '#5A6078',
    coral: '#FF7A59',
    lime: '#8BC34A',
    sky: '#35B8F0',
    indigo: '#3F51B5',
    plum: '#9C27B0',
    sand: '#C8A165',
  },
  // Chart series palette — A3 Cool contrast (D58, Jared 2026-10-17). `actual` is the brand pink,
  // so the spending heatmap stays pink (Figma: alias of color/primary); comparison / plan / last
  // month, the seasonal baseline and the projection take the cool category hues. Pace states
  // (ahead / behind / over) keep colors.status.
  chart: {
    actual: '#FF5D82',    // = primary
    comparison: '#4A90E2',
    baseline: '#2BB3A3',
    projection: '#7A5CFF',
  },
};

export const spacing = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 };
// D148: the one border width. Neutral and pink outlines use it alike; only the colour differs.
export const strokes = { default: 1 };
export const radii = { sm: 8, md: 12, lg: 20, xl: 28, pill: 999 }; // xl: Android M3 bottom-sheet top corners (D62, confirmed by D65)
export const typography = {
  fontFamily: 'Quicksand',
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  // Note: Figma defines h1: 28 and h4: 20; added here for parity
  sizes: { xs: 12, sm: 14, md: 16, lg: 18, xl: 20, h4: 20, h3: 24, h2: 28, h1: 28, metricLabel: 14, metricValue: 32 },
  // Line heights in px, one per size key. Read from the DS 1.x `Type/*` styles
  // (Figma cnZG7mOz3yGr3FyknnDsq8, 2026-10-17). metricValue was "auto" there and
  // metricLabel had no style: 40 and 20 are DS 2.0 B1 additions for Jared to confirm.
  lineHeights: { xs: 16, sm: 20, md: 22, lg: 24, xl: 26, h4: 26, h3: 30, h2: 34, h1: 34, metricLabel: 20, metricValue: 40 },
  // iOS Dynamic Type: the UIFont.TextStyle each ramp step scales relative to
  // (BudgitTypography.uiFont(textStyle:)). Large text wraps and never truncates
  // labels; chrome caps at the sizes in BudgitTypography (DS 2.0 B1, 2026-10-17).
  dynamicType: { xs: 'caption1', sm: 'subheadline', md: 'body', lg: 'headline', xl: 'title3', h4: 'title3', h3: 'title2', h2: 'title1', h1: 'title1', metricLabel: 'subheadline', metricValue: 'largeTitle' },
};

// Editable controls share one shape and mode-aware palette across clients.
// Heights are minimums. Dynamic Type and multiline content may grow the control.
export const input = {
  radius: radii.pill,
  minHeight: 56,
  paddingHorizontal: spacing.lg,
  paddingVertical: spacing.md,
  labelGap: spacing.sm,
  // The filled surface defines the field. Only focus adds a pink ring.
  borderWidth: 0,
  focusBorderWidth: 1,
  light: {
    background: colors.light.surfaceElevated,
    foreground: colors.light.text.primary,
    placeholder: colors.light.text.secondary,
    border: colors.light.text.secondary,
    focus: colors.primary,
  },
  dark: {
    background: colors.dark.surfaceElevated,
    foreground: colors.dark.text.primary,
    placeholder: colors.dark.text.secondary,
    border: colors.dark.text.secondary,
    focus: colors.primary,
  },
};

export const DEFAULT_CATEGORY_COLOR = colors.primary;

export const CATEGORY_COLOR_OPTIONS = [
  { label: 'Budgit pink', value: colors.primary },
  { label: 'Navy', value: colors.secondary },
  { label: 'Green', value: colors.status.ready },
  { label: 'Amber', value: colors.status.syncing },
  { label: 'Bitcoin', value: colors.bitcoin },
  { label: 'Blue', value: '#4A90E2' },
  { label: 'Teal', value: '#2BB3A3' },
  { label: 'Violet', value: '#7A5CFF' },
  { label: 'Rose', value: '#D93A5C' },
  { label: 'Slate', value: '#5A6078' },
];

const HEX_CATEGORY_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export const normalizeCategoryColor = (color) => (
  typeof color === 'string' && HEX_CATEGORY_COLOR_PATTERN.test(color)
    ? color
    : DEFAULT_CATEGORY_COLOR
);

// Button design tokens - standardized across web and mobile
// Reference: "I'm Set" button in web alerts
// All buttons must match this standard to ensure UI consistency
export const button = {
  borderRadius: {
    web: '50px',      // Web uses string for CSS
    mobile: 999,      // Mobile uses number for RN (equivalent to fully rounded)
  },
  padding: {
    vertical: {
      web: 0.75,      // MUI spacing units (0.75 * 8 = 6px)
      mobile: 18,     // React Native px (increased to match Cash App button height)
    },
    horizontal: {
      web: 2,         // MUI spacing units (2 * 8 = 16px)
      mobile: 16,     // React Native px
    },
  },
  fontSize: {
    web: '0.95rem',   // ~15.2px
    mobile: 16,       // React Native px
  },
  fontWeight: '600',
  textTransform: 'none',
  shadows: {
    web: 'none',      // No drop shadows on buttons
    mobile: 'none',   // No elevation on buttons
  },
  touchFeedback: {
    activeOpacity: 0.7,  // Mobile touch feedback (dims to 70% on press)
  },
};

// Curated list of common IANA timezones for shared pickers
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)' },
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  { value: 'America/Mexico_City', label: 'Central Time (Mexico City)' },
  { value: 'America/Sao_Paulo', label: 'Brasilia Time (São Paulo)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
  { value: 'Europe/London', label: 'GMT / BST (London)' },
  { value: 'Europe/Paris', label: 'Central European (Paris)' },
  { value: 'Europe/Berlin', label: 'Central European (Berlin)' },
  { value: 'Europe/Amsterdam', label: 'Central European (Amsterdam)' },
  { value: 'Europe/Rome', label: 'Central European (Rome)' },
  { value: 'Europe/Madrid', label: 'Central European (Madrid)' },
  { value: 'Europe/Moscow', label: 'Moscow Time' },
  { value: 'Asia/Dubai', label: 'Gulf Time (Dubai)' },
  { value: 'Asia/Kolkata', label: 'India Time (Mumbai)' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok)' },
  { value: 'Asia/Singapore', label: 'Singapore Time' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time' },
  { value: 'Asia/Shanghai', label: 'China Time (Shanghai)' },
  { value: 'Asia/Tokyo', label: 'Japan Time (Tokyo)' },
  { value: 'Asia/Seoul', label: 'Korea Time (Seoul)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern (Sydney)' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern (Melbourne)' },
  { value: 'Australia/Perth', label: 'Australian Western (Perth)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (Auckland)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

export const zIndices = { modal: 1300, popover: 1200, tooltip: 1500 };
export const shadows = {
  sm: '0px 1px 3px rgba(0,0,0,0.08)',
  md: '0px 4px 10px rgba(0,0,0,0.1)',
  lg: '0px 10px 24px rgba(0,0,0,0.12)',
};

// Chrome metrics and frame geometry shared by the Figma `Layout` collection and both
// apps (DS 2.0 plan §4, B1 2026-10-17). Sourced from shipped code where it exists.
export const layout = {
  frameWidth: 393,                          // design phone (docs/design/buddy/TOKENS.md)
  frameHeight: 852,
  gutter: spacing.md,                       // 16 — screen edge inset
  cardWidth: 361,                           // frameWidth - 2 * gutter
  tapTargetMin: 44,                         // minimum hit target on both platforms
  // iOS from shipped code: ACTIVITY_TAB_HEADER_HEIGHT (mobile/app/utils/activityLayout.ts), safe area
  // excluded, and TAB_BAR_HEIGHT (mobile/app/(tabs)/activity.tsx), home indicator included. Android is
  // Material 3 chrome (PLAN_OF_RECORD D59, Jared 2026-10-17): a flat 64 dp top app bar and an 80 dp
  // navigation bar with labels and an active indicator; shipped expo-router tabs measure 60 until it lands.
  headerHeight: { ios: 56, android: 64 },
  tabBarHeight: { ios: 85, android: 80 },
  sheetDetents: { medium: 0.66, large: 1 }, // fraction of screen height (iOS .medium/.large, Android bottom sheet)
};

// Motion tokens (DS 2.0 plan §4). Durations in ms. Springs are mass/stiffness/damping
// as Reanimated `withSpring` and SwiftUI `.interpolatingSpring` read them.
export const motion = {
  // `confetti` is the reward-burst duration (confetti, chest open, streak burst) — renamed from
  // `celebrate` (2026-10-18) so it is not confused with Buddy's `motion.celebrate` jump-twirl below.
  durations: { instant: 100, fast: 150, base: 250, slow: 400, confetti: 800 },
  springs: {
    standard: { mass: 0.9, stiffness: 270, damping: 30 },  // mobile/components/discover/DiscoverSheetFrame.tsx (sheet present)
    emphasized: { mass: 1, stiffness: 180, damping: 18 },  // mobile/components/budget-review/SwipeableCardDeck.tsx (card return)
    // Celebration only (PLAN_OF_RECORD D61, Jared 2026-10-18: "make him jump off the ground, twirl
    // around, very excited!"). Damping ratio 12 / (2 * sqrt(600 * 1)) = 0.24, natural frequency
    // 24.5 rad/s: ~45% first overshoot, three visible rebounds, settled (4 / (ζ·ω0)) in ~0.67 s, so the
    // landing wobble ends inside one celebrate beat. Was 1/400/14 (ratio 0.35, ~31% overshoot) —
    // too polite for a jump. Never use it for idle or everyday motion: Buddy is slow and soft there.
    bounce: { mass: 1, stiffness: 600, damping: 12 },
  },
  // Buddy's celebrate motion (D61). Uses the existing rig — the front sit with two hooves
  // (docs/design/buddy/lab, D52) — and transforms the WHOLE rig: no new pose, no side view.
  // Timings in ms from the start, distances in pt at the celebration-overlay size (full body
  // `bodyPt` tall); scale `jumpHeightPt` by renderedBodyPt / bodyPt for other sizes.
  // Squash/stretch scale about the hooves (bottom centre); the twirl rotates about the body centre.
  celebrate: {
    rig: 'front-sit',
    pose: 'happy',                 // face + hoof kick while airborne; C11 Celebrate replaces it once banked
    bodyPt: 140,
    anticipation: { durationMs: 120, easing: 'easeInOutSine', scaleX: 1.08, scaleY: 0.9 },  // crouch
    takeoff: { scaleX: 0.92, scaleY: 1.1 },                                               // stretch at launch
    jump: { heightPt: 56, riseMs: 260, riseEasing: 'easeOutCubic', fallMs: 260, fallEasing: 'easeInCubic' },
    twirl: { degrees: 360, durationMs: 480, startMs: 140, easing: 'easeInOutCubic', direction: 'clockwise' },
    landing: { scaleX: 1.14, scaleY: 0.84, settleSpring: 'bounce' },                       // squash, then springs.bounce back to 1
    hapticAtMs: 640,               // haptics.celebrate fires on touchdown (120 + 260 + 260)
    totalMs: 1320,                 // touchdown + the bounce spring settling (~680 ms)
    reducedMotion: 'hard-cut-to-pose', // no jump, no twirl, no haptics: cut straight to `pose`
  },
  // Reduce Motion: hard-cut to the end state, no loops, no haptics (docs/design/buddy/DECISIONS.md).
  reducedMotion: { durationMs: 0, loops: false, haptics: false },
};

// Named haptic patterns (DS 2.0 plan §4). Each beat maps to one platform call:
// kind 'impact'       → UIImpactFeedbackGenerator(style:) / Haptics.impactAsync(ImpactFeedbackStyle)
// kind 'notification' → UINotificationFeedbackGenerator(.type) / Haptics.notificationAsync(NotificationFeedbackType)
// kind 'selection'    → UISelectionFeedbackGenerator / Haptics.selectionAsync()
// `at` is ms from the pattern start. Patterns are skipped under Reduce Motion or haptics-off.
export const haptics = {
  selection: { beats: [{ at: 0, kind: 'selection' }] },
  success: { beats: [{ at: 0, kind: 'notification', type: 'success' }] },
  threshold: { beats: [{ at: 0, kind: 'impact', style: 'medium' }] },          // HomeCashEntryView thresholdHaptic
  error: { beats: [{ at: 0, kind: 'notification', type: 'error' }] },
  celebrate: { beats: [{ at: 0, kind: 'notification', type: 'success' }, { at: 120, kind: 'impact', style: 'heavy' }] },
  nudge: { beats: [{ at: 0, kind: 'impact', style: 'light' }] },
  snort: { beats: [{ at: 90, kind: 'impact', style: 'light' }, { at: 265, kind: 'impact', style: 'medium' }] }, // mobile/lib/welcomeMotion.ts
};
